import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { IconArrow } from 'design-system/icons';
import { CardView } from 'design-system/CardView';
import SectionHeader from 'design-system/SectionHeader';
import { TextButtonLinkSeeAll } from 'design-system/TextButton';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { getCorporateEvents } from '../actions';
import CorporateEventRow from './CorporateEventRow';
import { selectInvestmentCalendarShortList } from '../reducers/selectors';
import { stockScreenDefaultEventTypes } from '../constants';
const CorporateEventsSection = ({ assetIds, stockName, forPortfolio }) => {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const key = useMemo(() => {
        if (assetIds.length === 1 && !forPortfolio) {
            return assetIds[0];
        }
        return 'portfolio';
    }, [assetIds, forPortfolio]);
    const calendarEvents = useAppSelector((state) => selectInvestmentCalendarShortList(state, key));
    useEffect(() => {
        if (assetIds.length) {
            dispatch(getCorporateEvents(assetIds, key, undefined, key === 'portfolio' ? undefined : stockScreenDefaultEventTypes));
        }
    }, [assetIds, key, stockName]);
    const handleSeeAll = useCallback(() => {
        if (key === 'portfolio') {
            navigation.navigateDeprecated('InvestCorporateEvents', { assetIds, forPortfolio: true });
        }
        else if (stockName) {
            navigation.navigateDeprecated('InvestCorporateEvents', { assetIds, stockName, forPortfolio: false });
        }
    }, [assetIds, key, stockName]);
    const seeAllBtn = useMemo(() => {
        if (calendarEvents?.canSeeMore) {
            return <TextButtonLinkSeeAll onPress={handleSeeAll}/>;
        }
        return null;
    }, [calendarEvents?.canSeeMore, handleSeeAll]);
    if (!calendarEvents || !calendarEvents.canSeeMore || (key === 'portfolio' && !calendarEvents.events.length)) {
        return null;
    }
    if (!calendarEvents.events.length) {
        return (<>
        <SectionHeader largeVerticalMargins horizontalMargins button={seeAllBtn}>
          Corporate events
        </SectionHeader>
        <CardView noMarginTop noMarginBottom>
          <View style={styles.emptyContainer}>
            <Text TextThin-16 Secondary>
              There are no upcoming events
            </Text>
            <Spacer h={16}/>
            <TouchableBounce onPress={handleSeeAll}>
              <View style={styles.row}>
                <Text Text-16 Primary>
                  Previous events
                </Text>
                <Spacer w={8}/>
                <IconArrow color={colors.text.brand}/>
              </View>
            </TouchableBounce>
          </View>
        </CardView>
      </>);
    }
    return (<>
      <SectionHeader largeVerticalMargins horizontalMargins button={seeAllBtn}>
        Corporate events
      </SectionHeader>
      <CardView noMarginTop noMarginBottom>
        {calendarEvents.events.map((event, index) => (<CorporateEventRow event={event} key={String(index)}/>))}
      </CardView>
    </>);
};
const styles = StyleSheet.create({
    emptyContainer: {
        alignItems: 'center',
        paddingVertical: rem(12),
        paddingHorizontal: rem(16),
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
});
export default memo(CorporateEventsSection);
